import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';
import API_BASE_URL from '../config/api';

export const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

// Currency symbols and codes by country
const COUNTRY_CURRENCIES = {
  US: { code: 'USD', symbol: '$', name: 'US Dollar' },
  GB: { code: 'GBP', symbol: '£', name: 'British Pound' },
  EU: { code: 'EUR', symbol: '€', name: 'Euro' },
  DE: { code: 'EUR', symbol: '€', name: 'Euro' },
  FR: { code: 'EUR', symbol: '€', name: 'Euro' },
  IT: { code: 'EUR', symbol: '€', name: 'Euro' },
  ES: { code: 'EUR', symbol: '€', name: 'Euro' },
  JP: { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  CN: { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  IN: { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  CA: { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  AU: { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  BR: { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
  MX: { code: 'MXN', symbol: 'Mex$', name: 'Mexican Peso' },
  ZA: { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
  RU: { code: 'RUB', symbol: '₽', name: 'Russian Ruble' },
  KR: { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
  CH: { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  SE: { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
  NO: { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  DK: { code: 'DKK', symbol: 'kr', name: 'Danish Krone' },
  PL: { code: 'PLN', symbol: 'zł', name: 'Polish Zloty' },
  TN: { code: 'TND', symbol: 'د.ت', name: 'Tunisian Dinar' },
  EG: { code: 'EGP', symbol: 'E£', name: 'Egyptian Pound' },
  MA: { code: 'MAD', symbol: 'د.م.', name: 'Moroccan Dirham' },
  DZ: { code: 'DZD', symbol: 'د.ج', name: 'Algerian Dinar' },
  SA: { code: 'SAR', symbol: 'ر.س', name: 'Saudi Riyal' },
  AE: { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  TR: { code: 'TRY', symbol: '₺', name: 'Turkish Lira' },
  NG: { code: 'NGN', symbol: '₦', name: 'Nigerian Naira' },
  KE: { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling' },
  PK: { code: 'PKR', symbol: '₨', name: 'Pakistani Rupee' },
  BD: { code: 'BDT', symbol: '৳', name: 'Bangladeshi Taka' },
  PH: { code: 'PHP', symbol: '₱', name: 'Philippine Peso' },
  VN: { code: 'VND', symbol: '₫', name: 'Vietnamese Dong' },
  TH: { code: 'THB', symbol: '฿', name: 'Thai Baht' },
  ID: { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah' },
  MY: { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit' },
  SG: { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  NZ: { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar' },
  AR: { code: 'ARS', symbol: 'AR$', name: 'Argentine Peso' },
  CL: { code: 'CLP', symbol: 'CL$', name: 'Chilean Peso' },
  CO: { code: 'COP', symbol: 'COL$', name: 'Colombian Peso' },
  PE: { code: 'PEN', symbol: 'S/', name: 'Peruvian Sol' },
  IL: { code: 'ILS', symbol: '₪', name: 'Israeli Shekel' },
  HK: { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar' },
  TW: { code: 'TWD', symbol: 'NT$', name: 'Taiwan Dollar' },
};

// Default currency for worldwide or unknown countries
const DEFAULT_CURRENCY = { code: 'USD', symbol: '$', name: 'US Dollar' };

export const CurrencyProvider = ({ children }) => {
  const { user } = useAuth();
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [exchangeRates, setExchangeRates] = useState({});
  const [isWorldwide, setIsWorldwide] = useState(true);
  const [loading, setLoading] = useState(false);
  const [countryCode, setCountryCode] = useState(null);

  // Debug log on every render
  if (process.env.NODE_ENV !== 'production') {
    console.log('🔴 CurrencyProvider Render - User exists:', !!user);
    console.log('🔴 CurrencyProvider Render - User.settings:', user?.settings);
    console.log('🔴 CurrencyProvider Render - Scope:', user?.settings?.workMarketScope);
    console.log('🔴 CurrencyProvider Render - Location:', user?.settings?.workMarketLocation);
  }

  // Fetch exchange rates from API
  const fetchExchangeRates = async () => {
    try {
      setLoading(true);
      // Using free API - exchangerate-api.com (you can also use other services)
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
      const data = await response.json();
      setExchangeRates(data.rates);
      if (process.env.NODE_ENV !== 'production') {
        console.log('💱 Exchange rates updated:', Object.keys(data.rates).length, 'currencies');
      }
    } catch (error) {
      console.error('Error fetching exchange rates:', error);
      // Fallback rates if API fails
      setExchangeRates({
        USD: 1,
        EUR: 0.92,
        GBP: 0.79,
        JPY: 149.50,
        CAD: 1.36,
        AUD: 1.53,
        CHF: 0.88,
        CNY: 7.24,
        INR: 83.12,
        TND: 3.13,
        GBP: 0.79,
      });
    } finally {
      setLoading(false);
    }
  };

  // Update currency based on user's location/scope
  useEffect(() => {
    if (!user) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('💱 No user found');
      }
      return;
    }

    const scope = user?.settings?.workMarketScope;
    const location = user?.settings?.workMarketLocation;

    if (process.env.NODE_ENV !== 'production') {
      console.log('💱 Full user object:', user);
      console.log('💱 User settings:', user.settings);
      console.log('💱 Currency Update Check:', { 
        scope, 
        locationName: location?.locationName,
        lat: location?.lat,
        lng: location?.lng
      });
    }

    if (scope === 'worldwide') {
      setIsWorldwide(true);
      setCurrency(DEFAULT_CURRENCY);
      setCountryCode(null);
      if (process.env.NODE_ENV !== 'production') {
        console.log('💱 Set to Worldwide (USD)');
      }
    } else if (scope === 'my-country' && location && location.lat && location.lng) {
      setIsWorldwide(false);
      if (process.env.NODE_ENV !== 'production') {
        console.log('💱 Detecting currency for my-country...');
      }
      detectCurrencyFromLocation(location);
    } else {
      if (process.env.NODE_ENV !== 'production') {
        console.log('💱 No valid location, defaulting to USD. Scope:', scope, 'Location:', location);
      }
      setIsWorldwide(true);
      setCurrency(DEFAULT_CURRENCY);
    }
  }, [user, user?.settings?.workMarketScope, user?.settings?.workMarketLocation?.lat, user?.settings?.workMarketLocation?.lng, user?.settings?.workMarketLocation?.locationName]);

  // Fetch rates on mount and every 30 minutes
  useEffect(() => {
    fetchExchangeRates();
    const interval = setInterval(fetchExchangeRates, 30 * 60 * 1000); // 30 minutes
    return () => clearInterval(interval);
  }, []);

  const detectCurrencyFromLocation = async (location) => {
    try {
      if (process.env.NODE_ENV !== 'production') {
        console.log('💱 Detecting currency for location:', { lat: location.lat, lng: location.lng });
      }
      
      // Reverse geocode to get country code
      const response = await fetch(
        `${API_BASE_URL}/geo/reverse?lat=${encodeURIComponent(location.lat)}&lon=${encodeURIComponent(location.lng)}`
      );
      const payload = await response.json();
      const data = payload?.data || {};
      const detectedCountryCode = data.address?.country_code?.toUpperCase();
      
      if (process.env.NODE_ENV !== 'production') {
        console.log('💱 Detected country code:', detectedCountryCode);
      }
      
      if (detectedCountryCode && COUNTRY_CURRENCIES[detectedCountryCode]) {
        setCurrency(COUNTRY_CURRENCIES[detectedCountryCode]);
        setCountryCode(detectedCountryCode);
        if (process.env.NODE_ENV !== 'production') {
          console.log('💱 Currency set to:', COUNTRY_CURRENCIES[detectedCountryCode].code, COUNTRY_CURRENCIES[detectedCountryCode].symbol, 'for country:', detectedCountryCode);
        }
      } else {
        if (process.env.NODE_ENV !== 'production') {
          console.log('💱 No currency found for country:', detectedCountryCode, '- using USD');
        }
        setCurrency(DEFAULT_CURRENCY);
        setCountryCode(null);
      }
    } catch (error) {
      console.error('💱 Error detecting currency:', error);
      setCurrency(DEFAULT_CURRENCY);
      setCountryCode(null);
    }
  };

  // Convert amount from USD to user's currency
  const convertFromUSD = (amountInUSD) => {
    if (!amountInUSD || isNaN(amountInUSD)) return 0;
    if (currency.code === 'USD') return amountInUSD;
    
    const rate = exchangeRates[currency.code] || 1;
    return amountInUSD * rate;
  };

  // Convert amount from user's currency to USD
  const convertToUSD = (amount) => {
    if (!amount || isNaN(amount)) return 0;
    if (currency.code === 'USD') return amount;
    
    const rate = exchangeRates[currency.code] || 1;
    return amount / rate;
  };

  // Format amount with currency symbol
  const formatCurrency = (amount, options = {}) => {
    const {
      convertFromUSD: shouldConvert = true,
      showCode = false,
      decimals = 2
    } = options;

    const finalAmount = shouldConvert ? convertFromUSD(amount) : amount;
    
    const formatted = new Intl.NumberFormat(navigator.language || 'en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(finalAmount);

    if (showCode) {
      return `${currency.symbol}${formatted} ${currency.code}`;
    }
    return `${currency.symbol}${formatted}`;
  };

  const value = useMemo(() => ({
    currency,
    countryCode,
    setCurrency,
    exchangeRates,
    isWorldwide,
    loading,
    convertFromUSD,
    convertToUSD,
    formatCurrency,
    refreshRates: fetchExchangeRates,
  }), [currency, countryCode, exchangeRates, isWorldwide, loading]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
